import React from 'react';
import { Platform, Pressable, View } from 'react-native';
import { Feather } from '@expo/vector-icons';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import Text from '../../components/text/Text';
import { useTheme } from '../../theme';
import { useThemedStyles } from '../../hooks/useThemedStyles';

// On iPhones the app keeps the home-indicator strip free below the bar; a
// full-height bar on top of it left a tall blank band. There the bar is shorter
// and the app's bottom padding (navigation/AppNavigation) gives up this much of
// the strip to it (the indicator sits in its lowest ~13 pt). Android's inset can
// hold the system buttons, so it keeps the full bar.
export const HOME_INDICATOR_OVERLAP = 12;

/**
 * AppTabBar: the bottom bar (SPEC decision 4). Presentational only; the app
 * shell (components/navigation/MainTabBar) picks the tabs for the user's role
 * and renders it below every main-app screen, so screens never pass it.
 * Props:
 * - tabs: [{ key, label, icon (Feather name), active, onPress }]
 * - onLayout: reports the rendered height (the sync banner floats above it)
 * - style
 */
const AppTabBar = ({ tabs, onLayout, style }) => {
	const styles = useThemedStyles(createStyles);
	const { theme: { ui: { colors } } } = useTheme();
	const insets = useSafeAreaInsets();
	const isOverHomeIndicator = Platform.OS === 'ios' && insets.bottom > HOME_INDICATOR_OVERLAP;

	return (
		<View
			style={[styles.bar, isOverHomeIndicator && styles.barOverHomeIndicator, style]}
			onLayout={onLayout}
			accessibilityRole="tablist"
		>
			{tabs.map(tab => (
				<Pressable
					key={tab.key}
					onPress={tab.onPress}
					accessibilityRole="tab"
					accessibilityLabel={tab.label}
					accessibilityState={{ selected: Boolean(tab.active) }}
					style={({ pressed }) => [styles.tab, pressed && styles.pressed]}
				>
					<Feather name={tab.icon} size={22} color={tab.active ? colors.accent : colors.textSecondary} />
					<Text style={[styles.label, tab.active && styles.labelActive]} numberOfLines={1}>{tab.label}</Text>
				</Pressable>
			))}
		</View>
	);
};

const createStyles = ({ ui: { colors, type, space, layout } }) => ({
	bar: {
		flexDirection: 'row',
		height: layout.tabBarHeight,
		backgroundColor: colors.surface,
		borderTopWidth: 1,
		borderTopColor: colors.border,
	},
	barOverHomeIndicator: {
		height: layout.tabBarHeight - HOME_INDICATOR_OVERLAP,
	},
	tab: {
		flex: 1,
		minWidth: 0,
		alignItems: 'center',
		justifyContent: 'center',
		gap: space.xs,
		paddingHorizontal: space.xxs,
	},
	pressed: {
		opacity: 0.7,
	},
	label: {
		...type.caption,
		color: colors.textSecondary,
	},
	labelActive: {
		...type.label,
		color: colors.accent,
	},
});

export default AppTabBar;
